import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Plus, MessageCircle, Mail, MapPin } from "lucide-react";
import { useStore } from "./StoreShell";
import { StoreHeader } from "../../components/market/StoreHeader";
import { ProductSheet } from "../../components/market/ProductSheet";
import { money } from "../../lib/marketApi";

const ProductCard = ({ p, i, onOpen, onAdd, cur }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay: (i % 4) * 0.05 }}
    className="group flex flex-col overflow-hidden border border-black/5 shadow-sm transition-shadow hover:shadow-xl"
    style={{ background: "var(--mk-surface)", borderRadius: "var(--mk-radius)" }}
    data-testid={`product-card-${p.id}`}
  >
    <button onClick={onOpen} className="relative aspect-square overflow-hidden bg-slate-100" data-testid={`product-open-${p.id}`}>
      {p.images[0] && <img src={p.images[0]} alt={p.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />}
      {p.compare_at > p.price && (
        <span className="absolute left-2 top-2 rounded-full px-2 py-0.5 text-[11px] font-bold text-white" style={{ background: "var(--mk-accent)" }}>
          -{Math.round((1 - p.price / p.compare_at) * 100)}%
        </span>
      )}
      {p.stock <= 0 && <span className="absolute inset-0 flex items-center justify-center bg-white/70 text-sm font-semibold text-slate-700">Agotado</span>}
    </button>
    <div className="flex flex-1 flex-col gap-1 p-3">
      <p className="line-clamp-2 text-sm font-medium leading-snug">{p.name}</p>
      <div className="mt-auto flex items-end justify-between gap-2 pt-1">
        <div>
          <p className="text-base font-bold" data-testid={`product-price-${p.id}`}>{money(p.price, cur)}</p>
          {p.compare_at > p.price && <p className="text-xs line-through opacity-50">{money(p.compare_at, cur)}</p>}
        </div>
        <button
          disabled={p.stock <= 0}
          onClick={onAdd}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white shadow transition-transform active:scale-90 disabled:opacity-40"
          style={{ background: "var(--mk-primary)", color: "var(--mk-on-primary)" }}
          data-testid={`product-add-${p.id}`}
          aria-label="Agregar al carrito"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
    </div>
  </motion.div>
);

export default function Storefront() {
  const { store, products, addToCart } = useStore();
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("");
  const [open, setOpen] = useState(null);
  const t = store.texts || {};
  const c = store.contact || {};
  const cats = useMemo(() => [...new Set(products.map((p) => p.category).filter(Boolean))], [products]);
  const list = products.filter(
    (p) => (!cat || p.category === cat) && (!query || p.name.toLowerCase().includes(query.toLowerCase()))
  );

  if (store.status === "paused")
    return (
      <div className="flex min-h-screen items-center justify-center p-6 text-center" data-testid="store-paused">
        <p className="opacity-70">{store.name} está en pausa. Vuelve pronto.</p>
      </div>
    );

  return (
    <>
      <StoreHeader query={query} setQuery={setQuery} />
      <section className="mx-auto max-w-6xl px-4 pt-4">
        <div className="relative overflow-hidden" style={{ borderRadius: "calc(var(--mk-radius) + 6px)" }} data-testid="store-hero">
          {store.theme?.banner ? (
            <img src={store.theme.banner} alt="" className="absolute inset-0 h-full w-full object-cover" />
          ) : null}
          <div className="absolute inset-0" style={{ background: "linear-gradient(110deg, color-mix(in srgb, var(--mk-primary) 92%, black) 0%, color-mix(in srgb, var(--mk-primary) 55%, transparent) 60%, transparent 100%)" }} />
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="relative max-w-lg px-6 py-12 text-white sm:px-10 sm:py-20">
            <h1 className="text-3xl font-extrabold leading-tight sm:text-5xl" data-testid="store-hero-title">{t.hero_title}</h1>
            {t.hero_subtitle && <p className="mt-3 text-sm opacity-90 sm:text-base">{t.hero_subtitle}</p>}
            {t.hero_cta && (
              <a href="#productos" className="mt-6 inline-flex rounded-full bg-white px-6 py-3 text-sm font-semibold text-slate-900 shadow-lg transition-transform active:scale-95" data-testid="store-hero-cta">
                {t.hero_cta}
              </a>
            )}
          </motion.div>
        </div>
      </section>

      <section id="productos" className="mx-auto max-w-6xl scroll-mt-24 px-4 pb-16 pt-10">
        <h2 className="text-xl font-bold sm:text-2xl">{t.products_title || "Productos"}</h2>
        {cats.length > 0 && (
          <div className="-mx-4 mt-4 flex gap-2 overflow-x-auto px-4 pb-1" data-testid="store-categories">
            {["", ...cats].map((k) => (
              <button
                key={k || "all"}
                onClick={() => setCat(k)}
                className="shrink-0 rounded-full border px-4 py-2 text-sm font-medium transition-colors"
                style={cat === k ? { background: "var(--mk-primary)", color: "var(--mk-on-primary)", borderColor: "transparent" } : { borderColor: "rgba(0,0,0,.1)", background: "var(--mk-surface)" }}
                data-testid={`store-category-${k || "all"}`}
              >
                {k || "Todo"}
              </button>
            ))}
          </div>
        )}
        <div className="mt-5 grid grid-cols-2 gap-3 sm:gap-5 md:grid-cols-3 lg:grid-cols-4">
          {list.map((p, i) => (
            <ProductCard key={p.id} p={p} i={i} cur={store.currency} onOpen={() => setOpen(p)} onAdd={() => addToCart(p.id)} />
          ))}
        </div>
        {!list.length && <p className="py-16 text-center text-sm opacity-60" data-testid="store-empty">No hay productos que coincidan.</p>}
      </section>

      {(t.about || c.whatsapp || c.email || c.address) && (
        <section className="mx-auto max-w-6xl px-4 pb-16">
          <div className="grid gap-6 p-6 sm:grid-cols-2 sm:p-10" style={{ background: "var(--mk-surface)", borderRadius: "calc(var(--mk-radius) + 6px)" }}>
            <div>
              <h3 className="text-lg font-bold">{t.about_title || "Sobre nosotros"}</h3>
              <p className="mt-2 whitespace-pre-line text-sm opacity-75">{t.about}</p>
            </div>
            <div className="flex flex-col gap-3 text-sm">
              {c.whatsapp && (
                <a href={`https://wa.me/${c.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 font-medium" data-testid="store-contact-whatsapp">
                  <MessageCircle className="h-4 w-4" style={{ color: "var(--mk-primary)" }} /> WhatsApp
                </a>
              )}
              {c.email && (
                <a href={`mailto:${c.email}`} className="flex items-center gap-2 font-medium">
                  <Mail className="h-4 w-4" style={{ color: "var(--mk-primary)" }} /> {c.email}
                </a>
              )}
              {c.address && (
                <p className="flex items-start gap-2">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0" style={{ color: "var(--mk-primary)" }} /> {c.address}
                </p>
              )}
            </div>
          </div>
        </section>
      )}
      <footer className="border-t border-black/5 py-8 text-center text-xs opacity-60">
        © {new Date().getFullYear()} {store.name} · {t.footer}
      </footer>
      <ProductSheet product={open} onClose={() => setOpen(null)} />
    </>
  );
}
